import * as React from "react";
import Title from "../Common/Title";
import {
  getActiveCollections,
  getCollectionProducts,
  getUnpublishedCollections,
} from "../../Utils/vendor-customer-service";
import {
  extractIdFromGid,
  SHOPIFY_COLLECTION_STORE_URL,
  SHOPIFY_PRODUCT_STORE_URL,
} from "../../Utils/constant";
import {
  buildCategoryTree,
  divideCategories,
} from "../../Utils/divide-categories";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Slide,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const PAGE_SIZE = 50;
const PRODUCTS_PAGE_SIZE = 24;

const MODE_CONFIG = {
  published: {
    title: "Categories",
    subtitle: "Active (published) collections as a 3-level category tree",
    treeHeading: "Category Tree",
    fetchCollections: getActiveCollections,
    statusChip: { label: "Published", color: "success" },
    emptyParents: "No parent categories found.",
    emptySpecial: "No special collections found.",
    loadError: "Unable to load categories.",
  },
  unpublished: {
    title: "Unpublished Categories",
    subtitle:
      "Unpublished collections (not live on the online store) as a 3-level category tree",
    treeHeading: "Unpublished Category Tree",
    fetchCollections: getUnpublishedCollections,
    statusChip: { label: "Unpublished", color: "warning" },
    emptyParents: "No unpublished parent categories found.",
    emptySpecial: "No unpublished special collections found.",
    loadError: "Unable to load unpublished categories.",
  },
};

/** Visual tokens per hierarchy level */
const LEVEL_STYLES = {
  1: {
    label: "L1",
    chipColor: "primary",
    border: "#1976d2",
    bg: "rgba(25, 118, 210, 0.08)",
    text: "#0d47a1",
  },
  2: {
    label: "L2",
    chipColor: "secondary",
    border: "#9c27b0",
    bg: "rgba(156, 39, 176, 0.08)",
    text: "#6a1b9a",
  },
  3: {
    label: "L3",
    chipColor: "warning",
    border: "#ed6c02",
    bg: "rgba(237, 108, 2, 0.10)",
    text: "#e65100",
  },
};

const collectExpandableIds = (nodes) => {
  const ids = [];
  const walk = (list) => {
    (list || []).forEach((node) => {
      if (node.children?.length) {
        ids.push(String(node.collection?.id || node.collection?.mainId));
        walk(node.children);
      }
    });
  };
  walk(nodes);
  return ids;
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const openShopifyCollection = (collectionGid) => {
  const collectionId = extractIdFromGid(collectionGid);
  if (!SHOPIFY_COLLECTION_STORE_URL || !collectionId) {
    return;
  }
  const url = `${SHOPIFY_COLLECTION_STORE_URL}/${collectionId}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

const openShopifyProduct = (productGid) => {
  const productId = extractIdFromGid(productGid);
  if (!SHOPIFY_PRODUCT_STORE_URL || !productId) {
    return;
  }
  const url = `${SHOPIFY_PRODUCT_STORE_URL}/${productId}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

const TreeNodeLabel = ({ label, collection, level, childCount, onOpenProducts }) => {
  const productCount = collection?.productsCount?.count;
  const style = LEVEL_STYLES[level] || LEVEL_STYLES[1];

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        py: 0.75,
        px: 1,
        pr: 1,
        width: "100%",
        borderRadius: 1,
        borderLeft: `4px solid ${style.border}`,
        bgcolor: style.bg,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          flexGrow: 1,
          fontWeight: level === 1 ? 600 : level === 2 ? 500 : 400,
          color: style.text,
        }}
      >
        {label}
      </Typography>
      <Chip
        size="small"
        color={style.chipColor}
        label={style.label}
        sx={{ fontWeight: 700, minWidth: 40 }}
      />
      {childCount > 0 ? (
        <Chip
          size="small"
          variant="outlined"
          label={`${childCount} sub`}
          sx={{ borderColor: style.border, color: style.text }}
        />
      ) : null}
      {productCount != null ? (
        <Chip
          size="small"
          variant="outlined"
          clickable
          label={`${productCount} products`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenProducts?.(collection);
          }}
        />
      ) : null}
      <Button
        size="small"
        endIcon={<OpenInNewIcon />}
        disabled={
          !SHOPIFY_COLLECTION_STORE_URL || !extractIdFromGid(collection?.id)
        }
        onClick={(e) => {
          e.stopPropagation();
          openShopifyCollection(collection?.id);
        }}
      >
        Shopify
      </Button>
    </Stack>
  );
};

const renderTreeNodes = (nodes, level = 1, onOpenProducts) =>
  nodes.map((node) => {
    const itemId = String(node.collection?.id || node.collection?.mainId);
    const childCount = node.children?.length || 0;
    return (
      <TreeItem
        key={itemId}
        itemId={itemId}
        label={
          <TreeNodeLabel
            label={node.label}
            collection={node.collection}
            level={level}
            childCount={childCount}
            onOpenProducts={onOpenProducts}
          />
        }
      >
        {childCount
          ? renderTreeNodes(node.children, level + 1, onOpenProducts)
          : null}
      </TreeItem>
    );
  });

export default function Categories({ mode = "published" }) {
  const config = MODE_CONFIG[mode] || MODE_CONFIG.published;
  const [tree, setTree] = React.useState([]);
  const [otherCategories, setOtherCategories] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [expandedItems, setExpandedItems] = React.useState([]);

  const [productsOpen, setProductsOpen] = React.useState(false);
  const [selectedCollection, setSelectedCollection] = React.useState(null);
  const [products, setProducts] = React.useState([]);
  const [productsCursor, setProductsCursor] = React.useState(null);
  const [productsHasMore, setProductsHasMore] = React.useState(false);
  const [productsLoading, setProductsLoading] = React.useState(false);
  const [productsLoadingMore, setProductsLoadingMore] = React.useState(false);
  const [productsError, setProductsError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    const activeConfig = MODE_CONFIG[mode] || MODE_CONFIG.published;

    const fetchAll = async () => {
      setLoading(true);
      setError("");
      try {
        const all = [];
        let cursor = null;
        let hasNext = true;

        while (hasNext) {
          const response = await activeConfig.fetchCollections(
            PAGE_SIZE,
            cursor
          );
          if (response?.status !== 200) {
            throw new Error("bad_status");
          }
          const nodes = response?.data?.nodes || [];
          const pageInfo = response?.data?.pageInfo || {};
          all.push(...nodes);
          hasNext = Boolean(pageInfo.hasNextPage);
          cursor = pageInfo.endCursor || null;
          if (!hasNext) {
            break;
          }
        }

        if (cancelled) {
          return;
        }

        const divided = divideCategories(all);
        const nextTree = buildCategoryTree(divided);
        setTree(nextTree);
        setOtherCategories(divided.otherCategories || []);
        setTotalCount(all.length);
        // Expand all so L2/L3 colors are visible without hunting
        setExpandedItems(collectExpandableIds(nextTree));
      } catch (err) {
        if (!cancelled) {
          setError(activeConfig.loadError);
          setTree([]);
          setOtherCategories([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const fetchCollectionProducts = React.useCallback(
    async (collection, cursor = null, replace = true) => {
      if (!collection?.id) {
        setProductsError("Missing collection id.");
        return;
      }

      if (replace) {
        setProductsLoading(true);
      } else {
        setProductsLoadingMore(true);
      }
      setProductsError("");

      try {
        const response = await getCollectionProducts(
          collection.id,
          PRODUCTS_PAGE_SIZE,
          cursor,
        );
        if (response?.status !== 200) {
          throw new Error("bad_status");
        }

        const nodes = response?.data?.nodes || [];
        const pageInfo = response?.data?.pageInfo || {};
        const titleFromApi = response?.data?.collection?.title;

        setProducts((prev) => (replace ? nodes : [...prev, ...nodes]));
        setProductsCursor(pageInfo.endCursor || null);
        setProductsHasMore(Boolean(pageInfo.hasNextPage));
        if (titleFromApi) {
          setSelectedCollection((prev) =>
            prev
              ? {
                  ...prev,
                  title: titleFromApi,
                }
              : prev,
          );
        }
      } catch (err) {
        setProductsError("Unable to load products for this category.");
        if (replace) {
          setProducts([]);
          setProductsHasMore(false);
        }
      } finally {
        setProductsLoading(false);
        setProductsLoadingMore(false);
      }
    },
    [],
  );

  const openProductsDialog = (collection) => {
    setSelectedCollection(collection);
    setProducts([]);
    setProductsCursor(null);
    setProductsHasMore(false);
    setProductsOpen(true);
    fetchCollectionProducts(collection, null, true);
  };

  const closeProductsDialog = () => {
    setProductsOpen(false);
    setSelectedCollection(null);
    setProducts([]);
    setProductsCursor(null);
    setProductsHasMore(false);
    setProductsError("");
  };

  const loadMoreProducts = () => {
    if (!productsHasMore || productsLoadingMore || !selectedCollection) {
      return;
    }
    fetchCollectionProducts(selectedCollection, productsCursor, false);
  };

  const dialogTitle =
    selectedCollection?.title ||
    selectedCollection?.subTitle ||
    "Category products";

  return (
    <React.Fragment>
      <Title>{config.title}</Title>
      <Box sx={{ mt: 1, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="body1">{config.subtitle}</Typography>
          <Chip
            size="small"
            color={config.statusChip.color}
            label={config.statusChip.label}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {loading
            ? "Loading all collections…"
            : `${totalCount} collections · ${tree.length} parents · ${otherCategories.length} special`}
        </Typography>
      </Box>

      {error ? <Typography color="error">{error}</Typography> : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3} sx={{ pb: 4 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ sm: "center" }}
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography variant="h5">{config.treeHeading}</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setExpandedItems(collectExpandableIds(tree))}
                >
                  Expand all
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setExpandedItems([])}
                >
                  Collapse all
                </Button>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 1.5 }}
            >
              {[1, 2, 3].map((level) => {
                const style = LEVEL_STYLES[level];
                return (
                  <Chip
                    key={style.label}
                    size="small"
                    color={style.chipColor}
                    label={`${style.label} · Level ${level}`}
                    sx={{ fontWeight: 600 }}
                  />
                );
              })}
            </Stack>
            {tree.length === 0 ? (
              <Typography color="text.secondary">
                {config.emptyParents}
              </Typography>
            ) : (
              <SimpleTreeView
                expandedItems={expandedItems}
                onExpandedItemsChange={(event, itemIds) =>
                  setExpandedItems(itemIds)
                }
              >
                {renderTreeNodes(tree, 1, openProductsDialog)}
              </SimpleTreeView>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Special collections
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Titles starting with __
            </Typography>
            {otherCategories.length === 0 ? (
              <Typography color="text.secondary">
                {config.emptySpecial}
              </Typography>
            ) : (
              <Stack spacing={1}>
                {otherCategories.map((collection) => (
                  <Stack
                    key={collection.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                    }}
                  >
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {collection.title}
                    </Typography>
                    {collection?.productsCount?.count != null ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        clickable
                        label={`${collection.productsCount.count} products`}
                        onClick={() => openProductsDialog(collection)}
                      />
                    ) : null}
                    <Button
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      disabled={
                        !SHOPIFY_COLLECTION_STORE_URL ||
                        !extractIdFromGid(collection?.id)
                      }
                      onClick={() => openShopifyCollection(collection?.id)}
                    >
                      Shopify
                    </Button>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      )}

      <Dialog
        fullScreen
        open={productsOpen}
        onClose={closeProductsDialog}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={closeProductsDialog}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {dialogTitle}
            </Typography>
            <Button
              color="inherit"
              endIcon={<OpenInNewIcon />}
              disabled={
                !SHOPIFY_COLLECTION_STORE_URL ||
                !extractIdFromGid(selectedCollection?.id)
              }
              onClick={() => openShopifyCollection(selectedCollection?.id)}
            >
              Open collection
            </Button>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {products.length} loaded
            {productsHasMore ? " (more available)" : ""}
          </Typography>

          {productsError ? (
            <Typography color="error" sx={{ mb: 2 }}>
              {productsError}
            </Typography>
          ) : null}

          {productsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : products.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 8 }}>
              No products in this category.
            </Typography>
          ) : (
            <React.Fragment>
              <Grid container spacing={2}>
                {products.map((product) => {
                  const imageSrc = product?.featuredImage?.url;
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {imageSrc ? (
                          <CardMedia
                            component="img"
                            height="180"
                            image={imageSrc}
                            alt={
                              product?.featuredImage?.altText ||
                              product?.title ||
                              "Product"
                            }
                            sx={{ objectFit: "cover" }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 160,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "grey.100",
                            }}
                          >
                            <Typography color="text.secondary">
                              No image
                            </Typography>
                          </Box>
                        )}
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" gutterBottom>
                            {product?.title || "Untitled product"}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            {product?.status ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={product.status}
                              />
                            ) : null}
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Inv ${product?.totalInventory ?? 0}`}
                            />
                          </Stack>
                        </CardContent>
                        <CardActions
                          sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<OpenInNewIcon />}
                            disabled={
                              !SHOPIFY_PRODUCT_STORE_URL ||
                              !extractIdFromGid(product?.id)
                            }
                            onClick={() => openShopifyProduct(product?.id)}
                          >
                            Open in Shopify
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {productsHasMore ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={loadMoreProducts}
                    disabled={productsLoadingMore}
                  >
                    {productsLoadingMore ? "Loading…" : "Load more"}
                  </Button>
                </Box>
              ) : null}
            </React.Fragment>
          )}
        </Box>
      </Dialog>
    </React.Fragment>
  );
}
